import { useState } from 'react';
import { X, Loader2, ArrowRightLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { transferOwnership as transferOwnershipApi, TransferOwnershipRequest } from '../../../../api/pets';
import { toast } from 'sonner';

interface TransferOwnershipModalProps {
  petId: string;
  petName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function TransferOwnershipModal({
  petId,
  petName,
  onClose,
  onSuccess,
}: TransferOwnershipModalProps) {
  const { t } = useTranslation();
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [newOwnerDid, setNewOwnerDid] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const data: TransferOwnershipRequest = {};
    if (newOwnerEmail.trim()) data.newOwnerEmail = newOwnerEmail.trim();
    if (newOwnerDid.trim()) data.newOwnerDid = newOwnerDid.trim();
    if (!data.newOwnerEmail && !data.newOwnerDid) {
      toast.error(t('family.transfer.enterEmailOrDid', 'Enter new owner email or Petso DID.'));
      return;
    }

    setSubmitting(true);
    try {
      await transferOwnershipApi(petId, data);
      toast.success(t('family.transfer.success', 'Transfer initiated. New owner must accept.'));
      onSuccess();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(t('family.transfer.error', 'Failed to initiate transfer. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500/90 to-pink-500/90 dark:from-amber-600 dark:to-pink-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-6 h-6" />
              <h2 className="text-xl font-bold">{t('family.birth.transferOwnership', 'Transfer Ownership')}</h2>
            </div>
            <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1 rounded">
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-sm opacity-90 mt-1">{petName}</p>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-white/70">
            {t('family.transfer.description', 'Enter the new owner’s email or Petso user DID. They will need to accept the transfer. Parent links will remain unchanged.')}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
              {t('family.transfer.newOwnerEmail', 'New owner email')}
            </label>
            <input
              type="email"
              value={newOwnerEmail}
              onChange={(e) => setNewOwnerEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
              {t('family.transfer.newOwnerDid', 'Or Petso / DID')}
            </label>
            <input
              type="text"
              value={newOwnerDid}
              onChange={(e) => setNewOwnerDid(e.target.value)}
              placeholder="did:petso:..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white font-mono text-sm"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg text-gray-700 dark:text-white/90 hover:bg-gray-50 dark:hover:bg-white/5"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-pink-500 text-white font-medium hover:opacity-90 disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {submitting ? t('common.saving', 'Saving...') : t('family.transfer.submit', 'Initiate Transfer')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
